/** @file zlib inflate (RFC 1950 header and trailer, RFC 1951 blocks) into an exact-size buffer. */
import { buildHuffman, type HuffmanTable } from './buildHuffman';
import { copyBack } from './copyBack';
import { DEFLATE_TABLES } from './deflateTables.data';
import type { Cursor, NextSegment } from './inputSegments';

/** Why the zlib stream cannot become exactly `out.length` bytes. */
export type RawProblem = 'header' | 'damaged' | 'overflow' | 'truncated';

/**
 * The end of a complete stream: bytes written, the big-endian u32 after it (adler32), the input bytes read through
 * that trailer, and the bytes after it (counted only when asked; 0 otherwise).
 */
export interface RawEnd {
  out: Uint8Array;
  written: number;
  adler: number;
  consumed: number;
  trailing: number;
}

/** Decoder state: `out` is both the result and the window; the input cursor moves segment by segment. */
interface State extends Cursor {
  out: Uint8Array;
  written: number;
  next: NextSegment;
  /** Input bytes fetched so far. */
  read: number;
  /** Bit buffer, least significant bit first, and how many bits it holds. */
  bits: number;
  count: number;
  /** Zero bytes fed past the end of the input: consuming any of their bits means the stream was cut short. */
  over: number;
}

/** The decoding tables of one call, reused by every block, and the code lengths of a dynamic block. */
type Tables = Record<'lit' | 'dist' | 'codes' | 'fixedLit' | 'fixedDist', HuffmanTable> & { lens: Uint8Array };

const LEN_BASE = Uint16Array.from(DEFLATE_TABLES.lenBase);
const LEN_EXTRA = Uint8Array.from(DEFLATE_TABLES.lenExtra);
const DIST_BASE = Uint16Array.from(DEFLATE_TABLES.distBase);
const DIST_EXTRA = Uint8Array.from(DEFLATE_TABLES.distExtra);
const CL_ORDER = Uint8Array.from(DEFLATE_TABLES.codeLengthOrder);
const FIXED = new Uint8Array(320);
for (const [first, last, length] of DEFLATE_TABLES.fixedRuns) FIXED.fill(length, first, last + 1);
const EMPTY = new Uint8Array(0);
/** Throws the problem with the byte count reached (inflateExact reads both). */
function fail(s: State, problem: RawProblem): never {
  throw Object.assign(new Error(`inflate: ${problem}`), { problem, written: s.written });
}

/** Moves the input cursor to the next unread byte, segment by segment; false at the end of the input. */
function advance(s: State): boolean {
  while (s.at >= s.end) if (!s.next(s)) return false;
  return true;
}

/** The next input byte; past the end, a zero byte counted in `over`. */
function nextByte(s: State): number {
  if (s.at >= s.end && !advance(s)) {
    s.over++;
    return 0;
  }
  s.read++;
  return s.cur[s.at++] ?? 0;
}

/** Drops `n` bits; 'truncated' when that reaches the zero bytes fed past the end. */
function consume(s: State, n: number): void {
  s.bits >>>= n;
  s.count -= n;
  if (s.over !== 0 && s.over * 8 > s.count) fail(s, 'truncated');
}

/** The next `n` (≤ 16) bits as a number, least significant first. */
function take(s: State, n: number): number {
  while (s.count < n) {
    s.bits |= nextByte(s) << s.count;
    s.count += 8;
  }
  const value = s.bits & ((1 << n) - 1);
  consume(s, n);
  return value;
}

/** A code longer than the 9-bit lookup: canonical walk, one bit at a time (RFC 1951 §3.2.2). */
function slowSymbol(s: State, t: HuffmanTable): number {
  let code = 0;
  let first = 0;
  let index = 0;
  for (let len = 1; len <= 15; len++, code <<= 1) {
    code |= (s.bits >>> (len - 1)) & 1;
    const n = t.count[len] ?? 0;
    if (code - first < n) {
      consume(s, len);
      return t.symbol[index + code - first] ?? 0;
    }
    index += n;
    first = (first + n) << 1;
  }
  return fail(s, 'damaged');
}

/** Decodes one symbol of `t`. */
function symbolOf(s: State, t: HuffmanTable): number {
  while (s.count < 15) {
    s.bits |= nextByte(s) << s.count;
    s.count += 8;
  }
  const entry = t.fast[s.bits & 511] ?? 0;
  if (entry === 0) return slowSymbol(s, t);
  consume(s, entry & 15);
  return entry >> 4;
}

/** A stored block: aligned LEN, NLEN, then LEN bytes copied from the input (the bit buffer first). */
function stored(s: State): void {
  consume(s, s.count & 7);
  const len = take(s, 16);
  if (len !== (~take(s, 16) & 0xffff)) fail(s, 'damaged');
  if (s.written + len > s.out.length) fail(s, 'overflow');
  let left = len;
  for (; left > 0 && s.count >= 8; left--) s.out[s.written++] = take(s, 8);
  while (left > 0) {
    if (!advance(s)) fail(s, 'truncated');
    const n = Math.min(left, s.end - s.at);
    s.out.set(s.cur.subarray(s.at, s.at + n), s.written);
    s.at += n;
    s.read += n;
    s.written += n;
    left -= n;
  }
}

/** Match length of length symbol 257 + `i` (base plus extra bits). */
const lengthOf = (s: State, i: number): number => (LEN_BASE[i] ?? 0) + take(s, LEN_EXTRA[i] ?? 0);
/** Distance of distance symbol `d` (base plus extra bits). */
const distanceOf = (s: State, d: number): number => (DIST_BASE[d] ?? 0) + take(s, DIST_EXTRA[d] ?? 0);

/** One match: its length, then a distance within the bytes already written; never past the end of `out`. */
function copyMatch(s: State, sym: number, dist: HuffmanTable): void {
  const i = sym - 257;
  if (i >= 29) fail(s, 'damaged');
  const length = lengthOf(s, i);
  const d = symbolOf(s, dist);
  if (d >= 30) fail(s, 'damaged');
  const distance = distanceOf(s, d);
  if (distance > s.written) fail(s, 'damaged');
  if (s.written + length > s.out.length) fail(s, 'overflow');
  copyBack(s.out, s.written, distance, length);
  s.written += length;
}

/** The symbols of one Huffman block, until its end-of-block code; never writes past `out`. */
function huffmanBlock(s: State, lit: HuffmanTable, dist: HuffmanTable): void {
  const out = s.out;
  for (;;) {
    const sym = symbolOf(s, lit);
    if (sym < 256) {
      if (s.written >= out.length) fail(s, 'overflow');
      out[s.written++] = sym;
    } else if (sym === 256) return;
    else copyMatch(s, sym, dist);
  }
}

/** The literal/length and distance code lengths of a dynamic block, run-length coded with the code length code. */
function readLengths(s: State, t: Tables, total: number): void {
  const lens = t.lens;
  for (let i = 0; i < total; ) {
    const sym = symbolOf(s, t.codes);
    if (sym < 16) {
      lens[i++] = sym;
      continue;
    }
    if (sym === 16 && i === 0) fail(s, 'damaged');
    const value = sym === 16 ? (lens[i - 1] ?? 0) : 0;
    const repeat = sym === 16 ? 3 + take(s, 2) : sym === 17 ? 3 + take(s, 3) : 11 + take(s, 7);
    if (i + repeat > total) fail(s, 'damaged');
    lens.fill(value, i, i + repeat);
    i += repeat;
  }
}

/** A dynamic block's header (RFC 1951 §3.2.7) into `t.lit` and `t.dist`; 'damaged' for any invalid code set. */
function dynamicTables(s: State, t: Tables): void {
  const nlit = take(s, 5) + 257;
  const ndist = take(s, 5) + 1;
  const ncode = take(s, 4) + 4;
  if (nlit > 286 || ndist > 30) fail(s, 'damaged');
  t.lens.fill(0, 0, 19);
  for (let i = 0; i < ncode; i++) t.lens[CL_ORDER[i] ?? 0] = take(s, 3);
  if (!buildHuffman(t.codes, t.lens, 0, 19, false)) fail(s, 'damaged');
  readLengths(s, t, nlit + ndist);
  if ((t.lens[256] ?? 0) === 0 || !buildHuffman(t.lit, t.lens, 0, nlit, true) || !buildHuffman(t.dist, t.lens, nlit, ndist, true)) fail(s, 'damaged');
}

/** A reusable table for an alphabet of up to 288 symbols: 1,664 bytes. */
const table = (): HuffmanTable => ({ fast: new Uint16Array(512), count: new Uint16Array(16), symbol: new Uint16Array(288), next: new Uint16Array(16) });

/** RFC 1950 header (two whole bytes): deflate (CM 8), a window of at most 32 KiB, a valid FCHECK, no dictionary. */
function header(s: State): void {
  const cmf = nextByte(s);
  const flg = nextByte(s);
  if (s.over > 0 || (cmf & 15) !== 8 || cmf >> 4 > 7 || ((cmf << 8) | flg) % 31 !== 0 || (flg & 32) !== 0) fail(s, 'header');
}

/** The aligned big-endian u32 after the last block; the input read through it, and (when asked) what follows. */
function trailer(s: State, countTrailing: boolean): RawEnd {
  consume(s, s.count & 7);
  const adler = ((take(s, 8) << 24) | (take(s, 8) << 16) | (take(s, 8) << 8) | take(s, 8)) >>> 0;
  const buffered = (s.count >> 3) - s.over;
  let trailing = 0;
  for (let more = countTrailing; more; more = s.next(s)) trailing += s.end - s.at;
  return { out: s.out, written: s.written, adler, consumed: s.read - buffered, trailing: countTrailing ? trailing + buffered : 0 };
}

/**
 * Inflates the zlib stream whose segments `next` yields into a new `size`-byte buffer (allocated once the zlib header
 * is valid), never past its end, then reads the adler32 trailer. Throws an Error with `problem` and `written`; a stream
 * whose first two bytes are missing or not a zlib header is 'header'. Allocates the result and its tables (5 × 1,664 +
 * 320 = 8,640 bytes); work is proportional to the input read and the bytes written.
 */
export function inflateRaw(next: NextSegment, size: number, countTrailing: boolean): RawEnd {
  const s: State = { out: EMPTY, written: 0, next, cur: EMPTY, at: 0, end: 0, read: 0, bits: 0, count: 0, over: 0 };
  header(s);
  s.out = new Uint8Array(size);
  const t: Tables = { lit: table(), dist: table(), codes: table(), fixedLit: table(), fixedDist: table(), lens: new Uint8Array(320) };
  buildHuffman(t.fixedLit, FIXED, 0, 288, false);
  buildHuffman(t.fixedDist, FIXED, 288, 32, false);
  for (let last = 0; last === 0; ) {
    last = take(s, 1);
    const type = take(s, 2);
    if (type === 0) stored(s);
    else if (type === 1) huffmanBlock(s, t.fixedLit, t.fixedDist);
    else if (type === 2) {
      dynamicTables(s, t);
      huffmanBlock(s, t.lit, t.dist);
    } else fail(s, 'damaged');
  }
  return trailer(s, countTrailing);
}